import { SNLog } from '@Lib/Log';
import { c } from 'ttag';
import { MutationType, } from '@standardnotes/models';
import { isNullOrUndefined } from '@standardnotes/utils';
import { AbstractService, StorageValueModes, ApplicationStage, StorageKey, Challenge, ChallengeReason, ChallengePrompt, ChallengeValidation, MobileUnlockTiming, ApplicationEvent, ProtectionEvent, } from '@standardnotes/services';
import { ContentType } from '@standardnotes/domain-core';
import { isValidProtectionSessionLength } from './isValidProtectionSessionLength';
import { UnprotectedAccessSecondsDuration } from './UnprotectedAccessSecondsDuration';
/**
 * Enforces certain actions to require extra authentication,
 * like viewing a protected note, as well as managing how long that
 * authentication should be valid for.
 */
export class ProtectionService extends AbstractService {
    constructor(encryption, mutator, challenges, storage, internalEventBus) {
        super(internalEventBus);
        this.encryption = encryption;
        this.mutator = mutator;
        this.challenges = challenges;
        this.storage = storage;
        this.internalEventBus = internalEventBus;
        this.sessionExpiryTimeout = -1;
        this.mobilePasscodeTiming = MobileUnlockTiming.OnQuit;
        this.mobileBiometricsTiming = MobileUnlockTiming.OnQuit;
        this.isBiometricsSoftLockEngaged = false;
        this.applicationStarted = false;
    }
    deinit() {
        clearTimeout(this.sessionExpiryTimeout);
        this.encryption = undefined;
        this.challenges = undefined;
        this.storage = undefined;
        super.deinit();
    }
    async handleEvent(event) {
        if (event.type === ApplicationEvent.ApplicationStageChanged) {
            const stage = event.payload.stage;
            if (stage === ApplicationStage.LoadedDatabase_12) {
                this.updateSessionExpiryTimer(this.getSessionExpiryDate());
                this.mobilePasscodeTiming = this.getMobilePasscodeTiming();
                this.mobileBiometricsTiming = this.getMobileBiometricsTiming();
            }
        }
        else if (event.type === ApplicationEvent.Started) {
            this.applicationStarted = true;
        }
    }
    async isLocked() {
        if (!this.applicationStarted) {
            return true;
        }
        const isPasscodeLocked = await this.encryption.isPasscodeLocked();
        return isPasscodeLocked || this.isBiometricsSoftLockEngaged;
    }
    softLockBiometrics() {
        const challenge = new Challenge([new ChallengePrompt(ChallengeValidation.Biometric)], ChallengeReason.ApplicationUnlock, false);
        void this.challenges.promptForChallengeResponse(challenge);
        this.isBiometricsSoftLockEngaged = true;
        void this.notifyEvent(ProtectionEvent.BiometricsSoftLockEngaged);
        this.challenges.addChallengeObserver(challenge, {
            onComplete: () => {
                this.isBiometricsSoftLockEngaged = false;
                void this.notifyEvent(ProtectionEvent.BiometricsSoftLockDisengaged);
            },
        });
    }
    hasProtectionSources() {
        return this.encryption.hasAccount() || this.encryption.hasPasscode() || this.hasBiometricsEnabled();
    }
    hasUnprotectedAccessSession() {
        if (!this.hasProtectionSources()) {
            return true;
        }
        return this.getSessionExpiryDate() > new Date();
    }
    hasBiometricsEnabled() {
        const biometricsState = this.storage.getValue(StorageKey.BiometricsState, StorageValueModes.Nonwrapped);
        return Boolean(biometricsState);
    }
    enableBiometrics() {
        if (this.hasBiometricsEnabled()) {
            SNLog.onError(Error('Tried to enable biometrics when they already are enabled.'));
            return false;
        }
        this.storage.setValue(StorageKey.BiometricsState, true, StorageValueModes.Nonwrapped);
        return true;
    }
    async disableBiometrics() {
        if (!this.hasBiometricsEnabled()) {
            SNLog.onError(Error('Tried to disable biometrics when they already are disabled.'));
            return false;
        }
        if (await this.validateOrRenewSession(ChallengeReason.DisableBiometrics)) {
            this.storage.setValue(StorageKey.BiometricsState, false, StorageValueModes.Nonwrapped);
            return true;
        }
        else {
            return false;
        }
    }
    createLaunchChallenge() {
        const prompts = [];
        if (this.hasBiometricsEnabled()) {
            prompts.push(new ChallengePrompt(ChallengeValidation.Biometric));
        }
        if (this.encryption.hasPasscode()) {
            prompts.push(new ChallengePrompt(ChallengeValidation.LocalPasscode));
        }
        if (prompts.length > 0) {
            return new Challenge(prompts, ChallengeReason.ApplicationUnlock, false);
        }
        else {
            return undefined;
        }
    }
    async authorizeProtectedActionForItems(items, challengeReason) {
        let sessionValidation;
        const authorizedItems = [];
        for (const item of items) {
            const needsAuthorization = item.protected && !this.hasUnprotectedAccessSession();
            if (needsAuthorization && !sessionValidation) {
                sessionValidation = this.validateOrRenewSession(challengeReason);
            }
            if (!needsAuthorization || (await sessionValidation)) {
                authorizedItems.push(item);
            }
        }
        return authorizedItems;
    }
    async authorizeItemAccess(item) {
        if (!item.protected) {
            return true;
        }
        return this.authorizeAction(item.content_type === ContentType.TYPES.Note
            ? ChallengeReason.AccessProtectedNote
            : ChallengeReason.AccessProtectedFile, { fallBackToAccountPassword: true, requireAccountPassword: false, forcePrompt: false });
    }
    authorizeAddingPasscode() {
        return this.authorizeAction(ChallengeReason.AddPasscode, {
            fallBackToAccountPassword: true,
            requireAccountPassword: false,
            forcePrompt: false,
        });
    }
    authorizeChangingPasscode() {
        return this.authorizeAction(ChallengeReason.ChangePasscode, {
            fallBackToAccountPassword: true,
            requireAccountPassword: false,
            forcePrompt: false,
        });
    }
    authorizeRemovingPasscode() {
        return this.authorizeAction(ChallengeReason.RemovePasscode, {
            fallBackToAccountPassword: true,
            requireAccountPassword: false,
            forcePrompt: false,
        });
    }
    authorizeSearchingProtectedNotesText() {
        return this.authorizeAction(ChallengeReason.SearchProtectedNotesText, {
            fallBackToAccountPassword: true,
            requireAccountPassword: false,
            forcePrompt: false,
        });
    }
    authorizeFileImport() {
        return this.authorizeAction(ChallengeReason.ImportFile, {
            fallBackToAccountPassword: true,
            requireAccountPassword: false,
            forcePrompt: false,
        });
    }
    async authorizeBackupCreation() {
        return this.authorizeAction(ChallengeReason.ExportBackup, {
            fallBackToAccountPassword: true,
            requireAccountPassword: false,
            forcePrompt: false,
        });
    }
    async authorizeMfaDisable() {
        return this.authorizeActionWithChallengeResponse(ChallengeReason.DisableMfa, {
            fallBackToAccountPassword: true,
            requireAccountPassword: true,
            forcePrompt: true,
        });
    }
    async authorizeAutolockIntervalChange() {
        return this.authorizeAction(ChallengeReason.ChangeAutolockInterval, {
            fallBackToAccountPassword: true,
            requireAccountPassword: false,
            forcePrompt: false,
        });
    }
    async authorizeSessionRevoking() {
        return this.authorizeAction(ChallengeReason.RevokeSession, {
            fallBackToAccountPassword: true,
            requireAccountPassword: false,
            forcePrompt: false,
        });
    }
    async authorizeListedPublishing() {
        return this.authorizeAction(ChallengeReason.AuthorizeNoteForListed, {
            fallBackToAccountPassword: true,
            requireAccountPassword: false,
            forcePrompt: true,
        });
    }
    async authorizeAccountDeletion() {
        return this.authorizeActionWithChallengeResponse(ChallengeReason.DeleteAccount, {
            fallBackToAccountPassword: true,
            requireAccountPassword: true,
            forcePrompt: true,
        });
    }
    async authorizeAction(reason, dto) {
        return this.validateOrRenewSession(reason, dto);
    }
    async authorizeActionWithChallengeResponse(reason, dto) {
        return this.validateOrRenewSessionWithChallengeResponse(reason, dto);
    }
    getMobilePasscodeTimingOptions() {
        return [
            {
                title: c('B5.SecuritySync.Autolock.Label').t `Immediately`,
                key: MobileUnlockTiming.Immediately,
                selected: this.mobilePasscodeTiming === MobileUnlockTiming.Immediately,
            },
            {
                title: c('B5.SecuritySync.Autolock.Label').t `On Quit`,
                key: MobileUnlockTiming.OnQuit,
                selected: this.mobilePasscodeTiming === MobileUnlockTiming.OnQuit,
            },
        ];
    }
    getMobileBiometricsTimingOptions() {
        return [
            {
                title: c('B5.SecuritySync.Autolock.Label').t `Immediately`,
                key: MobileUnlockTiming.Immediately,
                selected: this.mobileBiometricsTiming === MobileUnlockTiming.Immediately,
            },
            {
                title: c('B5.SecuritySync.Autolock.Label').t `On Quit`,
                key: MobileUnlockTiming.OnQuit,
                selected: this.mobileBiometricsTiming === MobileUnlockTiming.OnQuit,
            },
        ];
    }
    getMobileBiometricsTiming() {
        return this.storage.getValue(StorageKey.MobileBiometricsTiming, StorageValueModes.Nonwrapped, MobileUnlockTiming.OnQuit);
    }
    getMobilePasscodeTiming() {
        return this.storage.getValue(StorageKey.MobilePasscodeTiming, StorageValueModes.Nonwrapped, MobileUnlockTiming.OnQuit);
    }
    setMobileBiometricsTiming(timing) {
        this.storage.setValue(StorageKey.MobileBiometricsTiming, timing, StorageValueModes.Nonwrapped);
        this.mobileBiometricsTiming = timing;
    }
    setMobilePasscodeTiming(timing) {
        this.storage.setValue(StorageKey.MobilePasscodeTiming, timing, StorageValueModes.Nonwrapped);
        this.mobilePasscodeTiming = timing;
    }
    setMobileScreenshotPrivacyEnabled(isEnabled) {
        return this.storage.setValue(StorageKey.MobileScreenshotPrivacyEnabled, isEnabled, StorageValueModes.Default);
    }
    getMobileScreenshotPrivacyEnabled() {
        return this.storage.getValue(StorageKey.MobileScreenshotPrivacyEnabled, StorageValueModes.Default, false);
    }
    async validateOrRenewSession(reason, { fallBackToAccountPassword = true, requireAccountPassword = false, forcePrompt = false } = {}) {
        const response = await this.validateOrRenewSessionWithChallengeResponse(reason, {
            fallBackToAccountPassword,
            requireAccountPassword,
            forcePrompt,
        });
        return response.success;
    }
    async validateOrRenewSessionWithChallengeResponse(reason, { fallBackToAccountPassword = true, requireAccountPassword = false, forcePrompt = false } = {}) {
        var _a;
        if (this.getSessionExpiryDate() > new Date() && !forcePrompt) {
            return { success: true };
        }
        const prompts = [];
        if (this.hasBiometricsEnabled()) {
            prompts.push(new ChallengePrompt(ChallengeValidation.Biometric));
        }
        if (this.encryption.hasPasscode()) {
            prompts.push(new ChallengePrompt(ChallengeValidation.LocalPasscode));
        }
        if (requireAccountPassword) {
            if (!this.encryption.hasAccount()) {
                throw Error('Requiring account password for challenge with no account');
            }
            prompts.push(new ChallengePrompt(ChallengeValidation.AccountPassword));
        }
        if (prompts.length === 0) {
            if (fallBackToAccountPassword && this.encryption.hasAccount()) {
                prompts.push(new ChallengePrompt(ChallengeValidation.AccountPassword));
            }
            else {
                return { success: true };
            }
        }
        const lastSessionLength = this.getLastSessionLength();
        const chosenSessionLength = isValidProtectionSessionLength(lastSessionLength)
            ? lastSessionLength
            : UnprotectedAccessSecondsDuration.OneMinute;
        prompts.push(new ChallengePrompt(ChallengeValidation.ProtectionSessionDuration, undefined, undefined, undefined, undefined, chosenSessionLength));
        const response = await this.challenges.promptForChallengeResponse(new Challenge(prompts, reason, true));
        if (response) {
            const length = (_a = response.values.find((value) => value.prompt.validation === ChallengeValidation.ProtectionSessionDuration)) === null || _a === void 0 ? void 0 : _a.value;
            if (isNullOrUndefined(length)) {
                SNLog.error(Error('No valid protection session length found. Got ' + length));
            }
            else {
                this.setSessionLength(length);
            }
            return { success: true, challengeResponse: response };
        }
        else {
            return { success: false };
        }
    }
    getSessionExpiryDate() {
        const expiresAt = this.storage.getValue(StorageKey.ProtectionExpirey);
        if (expiresAt) {
            return new Date(expiresAt);
        }
        else {
            return new Date();
        }
    }
    clearSession() {
        void this.setSessionExpiryDate(new Date());
        return this.notifyEvent(ProtectionEvent.UnprotectedSessionExpired);
    }
    setSessionExpiryDate(date) {
        this.storage.setValue(StorageKey.ProtectionExpirey, date);
    }
    getLastSessionLength() {
        return this.storage.getValue(StorageKey.ProtectionSessionLength);
    }
    setSessionLength(length) {
        this.storage.setValue(StorageKey.ProtectionSessionLength, length);
        const expiresAt = new Date();
        expiresAt.setSeconds(expiresAt.getSeconds() + length);
        this.setSessionExpiryDate(expiresAt);
        this.updateSessionExpiryTimer(expiresAt);
        void this.notifyEvent(ProtectionEvent.UnprotectedSessionBegan);
    }
    updateSessionExpiryTimer(expiryDate) {
        clearTimeout(this.sessionExpiryTimeout);
        const timer = () => {
            void this.clearSession();
        };
        this.sessionExpiryTimeout = setTimeout(timer, expiryDate.getTime() - Date.now());
    }
    async protectItems(items) {
        const protectedItems = await this.mutator.changeItems(items, (mutator) => {
            mutator.protected = true;
        }, MutationType.NoUpdateUserTimestamps);
        return protectedItems;
    }
    async unprotectItems(items, reason) {
        if (!(await this.authorizeAction(reason, {
            fallBackToAccountPassword: true,
            requireAccountPassword: false,
            forcePrompt: false,
        }))) {
            return undefined;
        }
        const unprotectedItems = await this.mutator.changeItems(items, (mutator) => {
            mutator.protected = false;
        }, MutationType.NoUpdateUserTimestamps);
        return unprotectedItems;
    }
    async protectNote(note) {
        const result = await this.protectItems([note]);
        return result[0];
    }
    async unprotectNote(note) {
        const result = await this.unprotectItems([note], ChallengeReason.UnprotectNote);
        return result ? result[0] : undefined;
    }
    async protectNotes(notes) {
        return this.protectItems(notes);
    }
    async unprotectNotes(notes) {
        const results = await this.unprotectItems(notes, ChallengeReason.UnprotectNote);
        return results || [];
    }
    async protectFile(file) {
        const result = await this.protectItems([file]);
        return result[0];
    }
    async unprotectFile(file) {
        const result = await this.unprotectItems([file], ChallengeReason.UnprotectFile);
        return result ? result[0] : undefined;
    }
}
